/**
 * pagefly-cart-bridge.js — Horizon (Shopify Standard Events) ⇄ PageFly Add to Cart
 *
 * Vấn đề: PageFly ATC chỉ POST /cart/add.js rồi báo cho pub/sub nội bộ của PageFly
 * (window.__pagefly_helper_store__). Horizon 4.x không lắng nghe pub/sub đó — theme chỉ
 * phản ứng với Shopify Standard Event `shopify:cart:lines-update` (cart-icon, cart-drawer,
 * cart-items, và custom script tgc-theme.js đều nghe event này). PageFly (pagefly-cart.js)
 * không có case cho "Horizon" và không phát Standard Event → theme không biết gì.
 *
 * Cách fix: subscribe vào đúng pub/sub mà PageFly dùng cho theme integration, rồi phát lại
 * CartLinesUpdateEvent y hệt cách product-form.js của Horizon làm sau khi add to cart.
 *
 * Cài: assets/pagefly-cart-bridge.js + 1 dòng <script type="module"> trong snippets/scripts.liquid
 * (xem hướng dẫn). Không jQuery, không polling, không MutationObserver; `@shopify/events`
 * đã nằm trong importmap của theme nên không tốn thêm request.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

// Chống đăng ký 2 lần (theme editor reload section, script bị include 2 nơi...)
if (!window.__pfCartBridge) {
  window.__pfCartBridge = true;

  const cartUrl = () => `${window.Theme?.routes?.cart_url || '/cart'}.js`;

  /**
   * Số lượng vừa thêm. Response của /cart/add.js trả `quantity` là tổng của line trong cart
   * (thêm cùng variant lần 2 sẽ là 2), nên ưu tiên đọc input quantity của form PageFly.
   */
  const addedQuantity = (item) => {
    const input = document.querySelector(
      `form.pf-product-form#product_form_${item.product_id} [name="quantity"]`
    );
    const qty = Number(input?.value);
    return qty > 0 ? qty : 1;
  };

  /** Được PageFly gọi với line item từ /cart/add.js khi ATC action = "Stay on page" (data-checkout="same"). */
  const onPageFlyAdd = (item) => {
    // store.set() gọi update() không có tham số → bỏ qua
    if (!item || !item.variant_id) return;

    try {
      const quantity = addedQuantity(item);
      const { promise, resolve, reject } = CartLinesUpdateEvent.createPromise();

      // 1) Báo cho theme NGAY (cart-drawer auto-open, cart-icon chờ promise...)
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'pagefly',
          lines: [{ merchandiseId: item.variant_id, quantity }],
          promise,
        })
      );

      // 2) Lấy cart mới để theme có totalQuantity chính xác (Horizon product-form cũng làm y vậy)
      fetch(cartUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`Cart fetch failed: ${res.status}`))))
        .then((cart) =>
          resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              source: 'pagefly',
              sourceId: `pagefly-${item.product_id}`,
              productId: item.product_id,
              itemCount: quantity,
              items: cart.items,
              didError: false,
            },
          })
        )
        .catch(reject);
    } catch (error) {
      // Không bao giờ để lỗi lọt ra ngoài → không làm hỏng các listener khác của PageFly
      console.warn('[pagefly-cart-bridge]', error);
    }
  };

  const init = () => {
    const store = window.__pagefly_helper_store__;
    if (!store || typeof store.subscribe !== 'function') return false;
    store.subscribe(onPageFlyAdd);
    return true;
  };

  // Store được tạo trong pagefly-general-helper.js (defer). Module này cũng deferred; nếu chạy
  // trước PageFly thì chờ DOMContentLoaded (mọi script defer đã chạy xong tại thời điểm đó).
  init() || document.addEventListener('DOMContentLoaded', init, { once: true });
}